import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from './i18n.js';
import { activateToeflBootcamp, cancelToeflBootcamp, fetchToeflBootcamp } from './api.js';
import './Toefl.css';

export const TOEFL_SETTINGS_PATH = '/settings?tab=toefl';

const REFUSAL_KEYS = {
  too_late_for_bootcamp: 'exams.toefl.activate.refuseTooSoon',
  too_soon: 'exams.toefl.activate.refuseTooSoon',
  english_below_b1: 'exams.toefl.activate.refuseBelowB1',
  below_b1: 'exams.toefl.activate.refuseBelowB1',
  diagnostic_too_weak: 'exams.toefl.activate.refuseWeakDiagnostic',
  diagnostic_below_b2: 'exams.toefl.activate.refuseBelowB2',
  diagnostic_incomplete: 'exams.toefl.activate.refuseWeakDiagnostic',
  weak_diagnostic: 'exams.toefl.activate.refuseWeakDiagnostic',
  starts_in_last_60_days: 'exams.toefl.activate.refuseTooFar',
  starts_in_last_30_days: 'exams.toefl.activate.refuseTooFar',
  too_far: 'exams.toefl.activate.refuseTooFar',
  exam_date_in_past: 'exams.toefl.activate.refusePastDate',
};

function normalizeRefusalReason(reason) {
  const raw = String(reason || '').toLowerCase();
  if (raw.includes('too_late') || raw === 'too_soon') return 'too_late_for_bootcamp';
  if (raw.includes('below_b1') || raw === 'below_b1') return 'english_below_b1';
  if (raw.includes('below_b2') || raw.includes('b2')) return 'diagnostic_below_b2';
  if (raw.includes('diagnostic')) return 'diagnostic_too_weak';
  if (raw.includes('last_60') || raw.includes('last_30') || raw === 'too_far') {
    return 'starts_in_last_60_days';
  }
  if (raw.includes('past')) return 'exam_date_in_past';
  return raw;
}

function formatExamDate(value) {
  const raw = String(value || '').slice(0, 10);
  if (!raw) return '';
  const parsed = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const STATUS_LABELS = {
  active: ['exams.toefl.activate.statusActive', 'TOEFL bootcamp in progress'],
  scheduled: ['exams.toefl.activate.statusScheduled', 'TOEFL starts in the last 2 months before the exam'],
  needs_diagnostic: ['exams.toefl.activate.statusNeedsDiagnostic', 'A short diagnostic is still needed'],
  refused: ['exams.toefl.activate.statusRefused', 'TOEFL bootcamp not started'],
};

export default function ToeflActivation({ embedded = false }) {
  const { t } = useTranslation('translation');
  const [targetDate, setTargetDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchToeflBootcamp()
      .then((payload) => {
        if (cancelled) return;
        setResult(payload);
        const existing = payload?.exam_date || payload?.examDate;
        if (existing) setTargetDate(String(existing).slice(0, 10));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = await activateToeflBootcamp({ exam_date: targetDate, target_date: targetDate });
      setResult(payload);
    } catch (err) {
      setError(err?.message || t('exams.toefl.activate.error', 'Could not activate the bootcamp.'));
    } finally {
      setSubmitting(false);
    }
  };

  const onUnenroll = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = await cancelToeflBootcamp();
      setResult(payload);
    } catch (err) {
      setError(err?.message || t('exams.toefl.staff.cancelError', 'Could not unenroll this student.'));
    } finally {
      setSubmitting(false);
    }
  };

  const status = String(result?.status || result?.result || '').toLowerCase();
  const refused = status === 'refused' || status === 'rejected' || Boolean(result?.refused);
  const activated = ['active', 'activated', 'ok', 'success'].includes(status) || Boolean(result?.activated);
  const scheduled = status === 'scheduled';
  const needsDiagnostic = status === 'needs_diagnostic';
  const enrolled = activated || scheduled || needsDiagnostic;
  const reason = normalizeRefusalReason(result?.reason || result?.refusal_reason || result?.refusalReason);
  const diagnosticResult = result?.diagnostic_result || result?.diagnosticResult || {};
  const skillDetails = Array.isArray(result?.skill_details || result?.skillDetails)
    ? (result.skill_details || result.skillDetails)
    : Object.entries(diagnosticResult.scores || {}).map(([skill, score]) => ({ skill, score }));

  const examDateLabel = formatExamDate(result?.exam_date || result?.examDate || targetDate);
  const statusCopy = STATUS_LABELS[status];
  const showStatus = Boolean(statusCopy) && status !== 'cancelled' && status !== 'none';

  if (!embedded) {
    return <Navigate to={TOEFL_SETTINGS_PATH} replace />;
  }

  return (
    <div className="toefl-settings" data-testid="toefl-activation">
      <p className="toefl-settings__intro">
        {t(
          'exams.toefl.activate.subtitle',
          'Enter your exam date. We then check whether you can start: your English is strong enough, and there is enough time before the exam. If yes, IELTS exam tasks are replaced by a TOEFL bootcamp of about 2 weeks to 2 months. Your IELTS progress is kept: you can come back to it whenever you want.',
        )}
      </p>

      {showStatus ? (
        <p className="toefl-settings__status" data-testid="toefl-settings-status">
          {t(statusCopy[0], statusCopy[1])}
          {examDateLabel ? ` · ${examDateLabel}` : ''}
        </p>
      ) : null}

      <form className="toefl-settings__form" onSubmit={onSubmit}>
        <label className="toefl-settings__label" htmlFor="toefl-exam-date">
          {t('exams.toefl.activate.dateLabel', 'Exam date')}
        </label>
        <input
          id="toefl-exam-date"
          className="toefl-input"
          type="date"
          min={minDate}
          required
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          data-testid="toefl-exam-date"
        />
        <div className="toefl-settings__actions">
          <button
            type="submit"
            className="toefl-btn toefl-btn--primary"
            disabled={submitting || !targetDate}
            data-testid="toefl-activate-submit"
          >
            {submitting
              ? t('exams.toefl.activate.submitting', 'Checking…')
              : t('exams.toefl.activate.submit', 'Check and enroll')}
          </button>
          {enrolled ? (
            <button
              type="button"
              className="toefl-btn toefl-btn--danger"
              disabled={submitting}
              onClick={() => void onUnenroll()}
              data-testid="toefl-settings-unenroll"
            >
              {t('exams.toefl.activate.returnToIelts', 'Back to IELTS')}
            </button>
          ) : null}
        </div>
      </form>

      {error ? (
        <div className="toefl-banner toefl-banner--error" data-testid="toefl-activate-error">
          {error}
        </div>
      ) : null}

      {refused ? (
        <div className="toefl-banner toefl-banner--warn" data-testid="toefl-activate-refused">
          <p>
            {t(
              REFUSAL_KEYS[reason] || 'exams.toefl.activate.refuseGeneric',
              reason === 'too_late_for_bootcamp' || reason === 'too_soon'
                ? 'Too late for a bootcamp (under 10 days). Your IELTS agenda is unchanged.'
                : reason === 'english_below_b1' || reason === 'below_b1'
                  ? 'Strengthen your English before the TOEFL bootcamp (below B1). Your IELTS agenda is unchanged.'
                  : reason === 'diagnostic_below_b2'
                    ? 'The diagnostic is below B2 (mean under 4.0/6, or more than one skill under 4.0). Stay on your IELTS plan for now.'
                  : reason === 'diagnostic_too_weak' || reason === 'weak_diagnostic'
                    ? 'The diagnostic is below B2. Stay on your IELTS plan for now.'
                    : reason === 'starts_in_last_60_days'
                      || reason === 'starts_in_last_30_days'
                      || reason === 'too_far'
                      ? 'The bootcamp starts in the last 2 months before the exam. Your IELTS agenda continues until then.'
                      : 'Bootcamp not started. Your IELTS agenda is unchanged.',
            )}
          </p>
          <p style={{ marginBottom: 0 }}>
            {t(
              'exams.toefl.activate.ieltsUnchanged',
              'This does not change your IELTS preparation plan.',
            )}
          </p>
          {skillDetails.length > 0 ? (
            <ul>
              {skillDetails.map((row) => (
                <li key={row.skill || row.name}>
                  {row.skill || row.name}: {row.score ?? row.estimate ?? '—'}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {scheduled ? (
        <div className="toefl-banner toefl-banner--info" data-testid="toefl-activate-scheduled">
          <p>
            {t(
              'exams.toefl.activate.scheduled',
              'Noted. The TOEFL bootcamp will start in the last 2 months before your exam. IELTS stays on the agenda until then.',
            )}
          </p>
        </div>
      ) : null}

      {needsDiagnostic ? (
        <div className="toefl-banner toefl-banner--info" data-testid="toefl-activate-diagnostic">
          <p>
            {t(
              'exams.toefl.activate.needsDiagnostic',
              'Complete the short diagnostic (one task per skill). You enroll only if the estimated level is at least B2.',
            )}
          </p>
          <Link
            className="toefl-btn toefl-btn--primary"
            to={result?.diagnostic_route || '/exams/toefl/diagnostic'}
            data-testid="toefl-diagnostic-link"
          >
            {t('exams.toefl.diagnostic.start', 'Start the short diagnostic')}
          </Link>
        </div>
      ) : null}

      {activated ? (
        <div className="toefl-banner toefl-banner--info" data-testid="toefl-activate-success">
          <p>
            {t(
              'exams.toefl.activate.success',
              'Bootcamp activated. Your daily agenda now focuses on TOEFL format tasks until the exam.',
            )}
          </p>
          <p style={{ marginBottom: 0 }}>
            {t(
              'exams.toefl.activate.progressKept',
              'Your IELTS progress is paused, not deleted. You can come back to it whenever you want from Settings.',
            )}
          </p>
          <div className="toefl-card" data-testid="toefl-american-english-card" style={{ marginTop: '1rem' }}>
            <p>
              {t(
                'exams.toefl.activate.americanEnglish',
                'Before you start writing or speaking: TOEFL materials use American spelling and American audio. IELTS accepts either system if you stay consistent. Open the short lesson on the pairs that change.',
              )}
            </p>
            <Link
              className="toefl-btn toefl-btn--primary"
              to="/exams/toefl/courses/american-english"
              data-testid="toefl-american-english-link"
            >
              {t('exams.toefl.activate.americanEnglishCta', 'American English vs IELTS')}
            </Link>
          </div>
        </div>
      ) : null}

      {status === 'cancelled' ? (
        <div className="toefl-banner toefl-banner--info" data-testid="toefl-activate-returned">
          <p style={{ marginBottom: 0 }}>
            {t(
              'exams.toefl.activate.returnedToIelts',
              'You are back on IELTS. Your previous IELTS progress is still there.',
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}
